<script setup lang="ts">
/**
 * Components-v2 „Container" — die Karte, die der echte Bot postet (Blueprint-
 * Vokabular: Container mit Akzentfarbe → Section mit Thumbnail rechts →
 * `## Überschrift` + Text → Separator → `-# Footer` → Buttons IM Container).
 * Anders als ein klassisches Embed sitzen Buttons innerhalb der Karte und der
 * Footer ist gedimmter Fließtext. Maße nach aktuellem Discord-Client.
 */
withDefaults(defineProps<{
  /** `## …` Heading der ersten TextDisplay */
  heading?: string
  /** Kleiner `-# …` Untertitel direkt unter dem Heading (Stripe-Karten) */
  subheading?: string
  accent?: string
  /** Thumbnail rechts im Section-Accessory: Clarify-Logo oder Bild-URL */
  thumbnail?: boolean | string
  /** `**Name**\nWert`-Paare, wie sie der Bot aus den Ticket-Inputs rendert */
  fields?: { name: string, value: string }[]
  /** `-# …` Footer-Zeile unter dem Separator */
  footer?: string
  /** Separator zwischen Body und Footer/Buttons (Default: sichtbar) */
  divider?: boolean
}>(), {
  heading: undefined,
  subheading: undefined,
  accent: 'var(--dc-gold)',
  thumbnail: false,
  fields: () => [],
  footer: undefined,
  divider: true
})
</script>

<template>
  <div class="dc-card" :style="{ '--card-accent': accent }">
    <div class="dc-card-section">
      <div class="dc-card-main">
        <div v-if="heading" class="dc-card-heading">{{ heading }}</div>
        <div v-if="subheading" class="dc-card-sub">{{ subheading }}</div>
        <div v-if="$slots.default" class="dc-card-text">
          <slot />
        </div>
        <div v-if="fields.length" class="dc-card-fields">
          <div v-for="f in fields" :key="f.name" class="dc-card-field">
            <span class="dc-card-field-name">{{ f.name }}</span>
            <span class="dc-card-field-value">{{ f.value }}</span>
          </div>
        </div>
      </div>
      <img
        v-if="thumbnail"
        class="dc-card-thumb"
        :src="typeof thumbnail === 'string' ? thumbnail : '/brand/clarify-icon-square.svg'"
        alt=""
        width="56"
        height="56"
      >
    </div>

    <div v-if="divider && (footer || $slots.actions)" class="dc-card-sep" />
    <div v-if="footer" class="dc-card-footer">{{ footer }}</div>
    <div v-if="$slots.actions" class="dc-card-actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
.dc-card {
  position: relative;
  max-width: 460px;
  margin-top: 6px;
  background: var(--dc-bg-sidebar);
  border: 1px solid var(--dc-border-card);
  border-radius: var(--dc-radius-msg);
  padding: 14px 16px 14px 18px;
  font-family: var(--font-discord);
  overflow: hidden;
}

.dc-card::before {
  content: '';
  position: absolute;
  inset-block: 0;
  left: 0;
  width: 4px;
  background: var(--card-accent);
}

.dc-card-section {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.dc-card-main {
  flex: 1;
  min-width: 0;
}

/* `## …` — Discord rendert Markdown-H2 in Nachrichten mit 1.25em */
.dc-card-heading {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--dc-ink-header);
}

/* `-# …` Subtext */
.dc-card-sub {
  margin-top: 2px;
  font-size: 0.78rem;
  color: var(--dc-ink-muted);
}

.dc-card-text {
  margin-top: 8px;
  font-size: 0.86rem;
  line-height: 1.45;
  color: var(--dc-ink);
}

.dc-card-text :deep(strong) {
  color: var(--dc-ink-header);
  font-weight: 700;
}

.dc-card-text :deep(em) {
  font-style: italic;
}

.dc-card-text :deep(.dc-mention) {
  background: var(--dc-mention);
  color: #c9cdfb;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 500;
}

.dc-card-text :deep(code) {
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 4px;
  padding: 1px 5px;
  font-family: var(--font-mono);
  font-size: 0.8rem;
}

/* `**Name**⏎Wert` — Inputs/Lookups als gestapelte Paare */
.dc-card-fields {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 10px;
}

.dc-card-field {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.dc-card-field-name {
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--dc-ink-header);
}

.dc-card-field-value {
  font-size: 0.86rem;
  color: var(--dc-ink);
  overflow-wrap: anywhere;
}

.dc-card-thumb {
  flex-shrink: 0;
  border-radius: 10px;
  display: block;
}

.dc-card-sep {
  height: 1px;
  background: var(--dc-border-card);
  margin: 12px 0 10px;
}

.dc-card-footer {
  font-size: 0.74rem;
  color: var(--dc-ink-muted);
}

.dc-card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.dc-card-footer + .dc-card-actions {
  margin-top: 10px;
}
</style>
